/**
 * Detail-page status (web plan W0.2): what a brand / shop / store / promotion
 * page renders, and what it tells crawlers, for each outcome of its query.
 *
 *  - `loading`   skeleton. Robots are left as they are (the previous page's,
 *                or the server's on a fresh load); the title is
 *                "{Type} · PromoStork", never the homepage title.
 *  - `ok`        the entity; the page builds its own head from it.
 *  - `not_found` `noindex` + a "{Type} not found" H1. Only a real "this entity
 *                is not there" answer gets here (`isNotFoundError`), a null
 *                result, or an id that no `*ById` query can resolve.
 *  - `error`     429 / 5xx / network / timeout, once apollo.ts's RetryLink has
 *                given up: a retry button, robots and canonical unchanged,
 *                title "{Type} · PromoStork". Client errors never downgrade
 *                robots (web plan, design decisions).
 *
 * Pure (no React, no Apollo client), so the classification is unit-tested on
 * its own.
 */
import type { PageHeadOptions } from '@/seo/clientHead';
import { errorCode, rawMessage } from '@/utils/validation/authErrors';

export type DetailStatus = 'loading' | 'ok' | 'not_found' | 'error';

export type DetailKind = 'brand' | 'shop' | 'store' | 'promotion';

/** The `{Type}` in titles and headings. */
export const DETAIL_TYPE_LABELS: Readonly<Record<DetailKind, string>> = {
  brand: 'Brand',
  shop: 'Shop',
  store: 'Store',
  promotion: 'Promotion',
};

/** A Mongo ObjectId: 24 hex characters. */
export const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

/**
 * True for the only key a `*ById` query can resolve. Anything else is a 404
 * without a round trip: the backend's B0.3 guard answers NOT_FOUND for it, and
 * until that ships it throws a CastError. Any other route key (a slug, web
 * plan W0.16) goes through `resolveEntityPath` first (`useDetailQuery` in
 * @/hooks/useAppData), whose 404 / 410 are the not-found page.
 */
export function isObjectId(value: unknown): value is string {
  return typeof value === 'string' && OBJECT_ID_RE.test(value);
}

/**
 * The exact message apollo.ts's errorLink turns into a sign-in redirect (and
 * the mobile app matches too). It means "your session is gone", never "this
 * entity is gone", so it must not become a noindex 404.
 */
export const AUTH_REQUIRED_MESSAGE = 'Authentication Must Be Provided';

/**
 * `extensions.code` values that mean the entity is not there:
 *  - `NOT_FOUND` / `GONE`: the backend's detail-query contract (B0.3, B0.9) —
 *    never existed, draft, blocked, hidden parent, other country / deleted,
 *    promotion ended > 90 days;
 *  - `404` / `410`: today's `ApolloError('{Type} Not Found', '404')` puts the
 *    HTTP status in the code as a string.
 */
const NOT_FOUND_CODES: ReadonlySet<string> = new Set(['NOT_FOUND', 'GONE', '404', '410']);

/**
 * Messages that mean the same, for answers whose code is missing or generic:
 *  - `{Type} Not Found` / `{Type} Gone` (today's and B0.3's wording);
 *  - `Authentication Must Be Provided for unpublished brand|promotion`: what
 *    `*ById` throws at an anonymous visitor for a draft until B0.3;
 *  - a Mongoose CastError (`INTERNAL_SERVER_ERROR`) for a malformed id.
 */
const NOT_FOUND_MESSAGES: readonly RegExp[] = [
  /^(brand|shop|store|promotion) (not found|gone)$/i,
  /^authentication must be provided for unpublished \w+$/i,
  /^cast to objectid failed\b/i,
];

/**
 * True when a detail query's error says the entity does not exist or is not
 * public. Everything else — the rate limiter's 429, a 5xx, a network failure
 * or timeout, the exact auth error, a schema mismatch — is a transient `error`.
 */
export function isNotFoundError(error: unknown): boolean {
  if (!error) return false;
  const { message, isNetwork } = rawMessage(error);
  // A request that never got a GraphQL answer says nothing about the entity.
  if (isNetwork) return false;
  if (message.trim() === AUTH_REQUIRED_MESSAGE) return false;

  const code = errorCode(error);
  if (code !== undefined && NOT_FOUND_CODES.has(code.toUpperCase())) return true;
  return NOT_FOUND_MESSAGES.some((pattern) => pattern.test(message.trim()));
}

export interface DetailOutcome {
  /** The resolved entity, or null. */
  item: unknown;
  loading: boolean;
  error: unknown;
  /** False when the route param cannot identify an entity at all. */
  validParam: boolean;
}

/**
 * The page status for one query outcome. Order matters:
 *  1. an unusable param is a 404 (the query is skipped);
 *  2. an error wins over data (errorPolicy 'none' leaves none anyway);
 *  3. data wins over loading, so a cache hit renders at once while
 *     cache-and-network refreshes it;
 *  4. a finished query with no entity is a 404.
 */
export function detailStatus({ item, loading, error, validParam }: DetailOutcome): DetailStatus {
  if (!validParam) return 'not_found';
  if (error) return isNotFoundError(error) ? 'not_found' : 'error';
  if (item) return 'ok';
  if (loading) return 'loading';
  return 'not_found';
}

/**
 * `<PageHead/>` options for every status but `ok` (the page builds those from the
 * entity). Pages spread them over their own options, so the entity-derived
 * fields they leave undefined fall back to the site defaults.
 */
export function detailSeoOptions(kind: DetailKind, status: DetailStatus): PageHeadOptions {
  const label = DETAIL_TYPE_LABELS[kind];
  if (status === 'not_found') return { title: `${label} not found`, noindex: true };
  // Loading and failed loads: say what the page is, and leave robots alone.
  // The canonical stays the page's own (the pathname default).
  return { title: label, keepRobots: true };
}
